import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { recommendationValidationVal001Api, type SprintDashboard, type SprintValidation, type Val001Status } from '../../api/recommendation-validation-val001';

type Tab = 'overview' | 'modules' | 'lifecycle' | 'providers' | 'jobs' | 'gates' | 'status';

export function RecommendationValidationVal001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [stages, setStages] = useState<{ key: string; name: string; order: number }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Val001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-violet-200 bg-white dark:border-violet-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-violet-700 dark:text-violet-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, stg, prov, jb, gt, st] = await Promise.all([
        recommendationValidationVal001Api.dashboard(), recommendationValidationVal001Api.validationModules(),
        recommendationValidationVal001Api.lifecycleStages(), recommendationValidationVal001Api.providers(),
        recommendationValidationVal001Api.backgroundJobs(), recommendationValidationVal001Api.qualityGates(),
        recommendationValidationVal001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setStages(stg); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await recommendationValidationVal001Api.validate({
        every_recommendation_measurable: true, every_recommendation_has_evidence: true,
        every_recommendation_evolves: true, confidence_updates_automatic: true,
        marketplace_dna_updated: true, knowledge_graph_updated: true, operating_brain_updated: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Recommendation Validation passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Validation Types' },
    { id: 'lifecycle', label: 'Lifecycle' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-violet-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>VAL-000001 · P0 · Mandatory Production Governance</p>
        <h1 className="text-2xl font-semibold">AI Recommendation Validation Framework</h1>
        <p className={`text-sm ${muted}`}>Generated → Approved → Published → Observed → Measured → Validated → Scored → Learnt → Archived</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/marketplace-operating-brain-dev110" className={`${accent} hover:underline`}>DEV-000110</Link>
          <span className={muted}>→</span>
          <Link to="/recommendation-validation" className={`${accent} hover:underline`}>Live Validation Platform</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-violet-300 bg-violet-50 p-3 text-sm text-violet-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-violet-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Every Recommendation Scientifically Validated</h2>
            <p className={`mt-2 text-sm ${muted}`}>Continuously prove recommendations improve customer outcomes. Platform becomes more accurate with every accepted or rejected recommendation.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Validation Types', value: dashboard.validation_modules },
              { label: 'Lifecycle Stages', value: dashboard.lifecycle_stages }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-violet-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Quality Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-violet-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}

      {tab === 'lifecycle' && stages.map((s) => (
        <div key={s.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{s.order}. {s.name}</p></div>
      ))}

      {tab === 'providers' && providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs ${muted}`}>{p.interface}</p></div>
      ))}

      {tab === 'jobs' && jobs.map((j) => (
        <div key={j.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{j.name}</p><p className={`text-xs ${muted}`}>{j.schedule}</p></div>
      ))}

      {tab === 'gates' && gates.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{g.name}</p>{g.blocking && <p className={`text-xs ${muted}`}>Blocking</p>}</div>
      ))}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}><pre className="overflow-auto text-xs">{JSON.stringify(status, null, 2)}</pre></div>
      )}
    </div>
  );
}
